import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { Input, Avatar, Tooltip, Dropdown, DatePicker, message, Modal, Pagination } from 'antd';
import {
  SearchOutlined,
  MoreOutlined,
  DownOutlined,
  TableOutlined,
  AppstoreOutlined,
  LockOutlined,
  CaretDownOutlined,
  EditOutlined,
  CheckOutlined,
  SaveOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import {
  parseIssuesQuery,
  filterTasks,
  isStatusClosed,
  priorityLabelColor,
  queryToServerParams,
} from './issuesSearchUtils';
import './IssuesListView.scss';


const OpenIcon = () => (
  <svg className="ghp-status-icon is-open" width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
    <path d="M8 9.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" />
    <path fillRule="evenodd" d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0ZM1.5 8a6.5 6.5 0 1 0 13 0 6.5 6.5 0 0 0-13 0Z" />
  </svg>
);

const ClosedIcon = () => (
  <svg className="ghp-status-icon is-closed" width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
    <path d="M11.28 6.78a.75.75 0 0 0-1.06-1.06L7.25 8.69 5.78 7.22a.75.75 0 0 0-1.06 1.06l2 2a.75.75 0 0 0 1.06 0l3.5-3.5Z" />
    <path fillRule="evenodd" d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0Zm-1.5 0a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0Z" />
  </svg>
);

const PALETTE = [
  { bg: '#ddf4ff', fg: '#0969da', border: '#b6e3ff' },
  { bg: '#dafbe1', fg: '#1a7f37', border: '#aceebb' },
  { bg: '#fff8c5', fg: '#9a6700', border: '#f5d88c' },
  { bg: '#ffebe9', fg: '#cf222e', border: '#ffcecb' },
  { bg: '#fbefff', fg: '#8250df', border: '#ecd4ff' },
  { bg: '#fff1e5', fg: '#bc4c00', border: '#ffd8b5' },
  { bg: '#eaeef2', fg: '#1f2328', border: '#d0d7de' },
];

const hashPalette = (str = '') => {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
};

const PRIORITY_PALETTE = {
  Low:    { bg: '#dafbe1', fg: '#1a7f37', border: '#aceebb' },
  Medium: { bg: '#ddf4ff', fg: '#0969da', border: '#b6e3ff' },
  High:   { bg: '#fff1e5', fg: '#bc4c00', border: '#ffd8b5' },
  Urgent: { bg: '#ffebe9', fg: '#cf222e', border: '#ffcecb' },
};

const STATUS_PALETTE = (name = '') => {
  if (/done|closed|completed/i.test(name)) return { bg: '#dafbe1', fg: '#1a7f37', border: '#aceebb' };
  if (/todo|backlog|new/i.test(name))      return { bg: '#ddf4ff', fg: '#0969da', border: '#b6e3ff' };
  if (/progress|doing|review/i.test(name)) return { bg: '#fff8c5', fg: '#9a6700', border: '#f5d88c' };
  if (/cancel|block/i.test(name))          return { bg: '#ffebe9', fg: '#cf222e', border: '#ffcecb' };
  return hashPalette(name);
};

const DEFAULT_PILL = { bg: '#eaeef2', fg: '#1f2328', border: '#d0d7de' };

const Pill = ({ label, palette, icon, className = '' }) => {
  if (!label) return null;
  const p = palette || DEFAULT_PILL;
  return (
    <span
      className={`ghp-pill ${className}`}
      style={{ background: p.bg, color: p.fg, borderColor: p.border }}
    >
      {icon}
      {label}
    </span>
  );
};

// Single built-in default view — shown only when a project is in context.
const DEFAULT_VIEW = { key: 'all', label: 'All Tasks', query: '', icon: 'table', builtin: true };

/** GitHub-style searchable popover for cell editing */
const FieldPopover = ({ title, options, value, multi, onChange, onClear, getDot }) => {
  const [q, setQ] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const shown = useMemo(() => {
    if (!q.trim()) return options;
    const needle = q.toLowerCase();
    return options.filter(o => (o.label || '').toLowerCase().includes(needle));
  }, [options, q]);
  useEffect(() => {
    setActiveIndex(0);
  }, [q, options]);
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector(`[data-idx="${activeIndex}"]`);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);
  useEffect(() => {
    // Antd Dropdown can steal focus back to its trigger; force focus into the
    // search input on mount so arrow keys land on our handler.
    const t = setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, []);
  const isSelected = (opt) => {
    if (multi) return Array.isArray(value) && value.includes(opt.value);
    return value === opt.value;
  };
  const handleKeyDown = (e) => {
    if (!shown.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % shown.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + shown.length) % shown.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = shown[activeIndex];
      if (opt) onChange(opt);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // Close the parent Antd Dropdown by simulating an outside click.
      document.body.click();
    }
  };
  return (
    <div
      className="ghp-popover"
      onClick={(e) => e.stopPropagation()}
    >
      {title && <div className="ghp-popover__head">{title}</div>}
      <div className="ghp-popover__search">
        <input
          ref={inputRef}
          autoFocus
          placeholder="Search…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </div>
      <div className="ghp-popover__list" ref={listRef}>
        {shown.length === 0 ? (
          <div className="ghp-popover__empty">No matches</div>
        ) : (
          shown.map((opt, idx) => {
            const active = idx === activeIndex;
            return (
              <div
                key={String(opt.value)}
                data-idx={idx}
                className={`ghp-popover__item ${isSelected(opt) ? 'is-selected' : ''} ${active ? 'is-active' : ''}`}
                style={active ? { background: '#ddf4ff' } : undefined}
                onClick={() => onChange(opt)}
              >
                <span className="ghp-check"><CheckOutlined /></span>
                {getDot && getDot(opt) && (
                  <span className="ghp-dot" style={{ background: getDot(opt) }} />
                )}
                <span className="ghp-item-label">{opt.label}</span>
              </div>
            );
          })
        )}
      </div>
      {onClear && (
        <div className="ghp-popover__foot">
          <button className="is-danger" type="button" onClick={onClear}>Clear</button>
        </div>
      )}
    </div>
  );
};

const AUTOCOMPLETE_FIELDS = [
  { key: 'status',   label: 'Status' },
  { key: 'type',     label: 'Type' },
  { key: 'priority', label: 'Priority' },
  { key: 'assignee', label: 'Assignee' },
];

const quoteIfNeeded = (v) => (/\s/.test(v) ? `"${v}"` : v);

const IssuesListView = ({
  tasks,
  loading,
  taskTypes,
  assignees,
  projects,
  showProjectFilter,
  projectId,
  canManageViews = false,
  currentPage,
  pageSize,
  totalCount,
  onPageChange,
  onOpenTask,
  onTaskUpdated,
  onServerSearch,
  initialQuery = '',
  initialViewKey = null,
  onViewChange,
}) => {
  // initialQuery / initialViewKey come from the URL (see Tasks.jsx); the query
  // is echoed back through onServerSearch and the view through onViewChange.
  const [activeViewKey, setActiveViewKey] = useState(
    projectId ? (initialViewKey || DEFAULT_VIEW.key) : null
  );
  const [query, setQuery] = useState(initialQuery || '');
  const lastEmittedQueryRef = useRef(initialQuery || '');

  const [titleEditId, setTitleEditId] = useState(null);
  const [titleDraft, setTitleDraft] = useState('');
  const titleInputRef = useRef(null);

  const [statusCache, setStatusCache] = useState({}); // projectId -> statuses
  const [taskTypeCache, setTaskTypeCache] = useState({}); // projectId -> types
  const [memberCache, setMemberCache] = useState({}); // projectId -> assignable members

  const [savedViews, setSavedViews] = useState([]);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveModalName, setSaveModalName] = useState('');
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameName, setRenameName] = useState('');

  // Autocomplete state
  const [caret, setCaret] = useState(0);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestIdx, setSuggestIdx] = useState(0);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (titleEditId && titleInputRef.current) titleInputRef.current.focus();
  }, [titleEditId]);

  // Translate the query box into server-side filter params (debounced) and
  // push them to the parent. Free text plus the status / type / priority /
  // assignee tokens all filter across the whole dataset on the backend —
  // not just the rows rendered on the current page.
  useEffect(() => {
    const params = queryToServerParams(query);
    const t = setTimeout(() => {
      lastEmittedQueryRef.current = query;
      onServerSearch && onServerSearch(params, query);
    }, 350);
    return () => clearTimeout(t);
  }, [query, onServerSearch]);

  // Follow the URL on back/forward (ignore the echo of our own update).
  useEffect(() => {
    const next = initialQuery || '';
    if (next !== lastEmittedQueryRef.current) {
      lastEmittedQueryRef.current = next;
      setQuery(next);
    }
  }, [initialQuery]);

  useEffect(() => {
    onViewChange && onViewChange(activeViewKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeViewKey]);

  // Load saved views for the current project
  useEffect(() => {
    if (!projectId) { setSavedViews([]); return; }
    axios.get(`/project_board/task-views/?project=${projectId}&page_size=100`)
      .then(r => setSavedViews(r.data.results || r.data || []))
      .catch(() => setSavedViews([]));
  }, [projectId]);

  // Auto-activate the built-in default view when switching into a project context.
  useEffect(() => {
    if (projectId && !activeViewKey) {
      setActiveViewKey(DEFAULT_VIEW.key);
      setQuery(DEFAULT_VIEW.query);
    }
    if (!projectId && activeViewKey) {
      setActiveViewKey(null);
      setQuery('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Prefetch status/type lists for the project so the autocomplete has values.
  useEffect(() => {
    if (!projectId) return;
    if (!statusCache[projectId]) {
      axios.get(`/project_board/statuses/?project=${projectId}`)
        .then(r => setStatusCache(prev => ({ ...prev, [projectId]: r.data.results || r.data || [] })))
        .catch(() => {});
    }
    if (!taskTypeCache[projectId]) {
      axios.get(`/project_board/task-types/?project=${projectId}`)
        .then(r => setTaskTypeCache(prev => ({ ...prev, [projectId]: r.data.results || r.data || [] })))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const allViews = useMemo(() => {
    const defaults = projectId ? [DEFAULT_VIEW] : [];
    const saved = (projectId ? savedViews : []).map(v => ({
      key: `saved-${v.id}`,
      label: v.name,
      query: v.query || '',
      id: v.id,
      icon: 'table',
      builtin: false,
    }));
    return [...defaults, ...saved];
  }, [savedViews, projectId]);

  const activeView = useMemo(
    () => allViews.find(v => v.key === activeViewKey) || allViews[0],
    [allViews, activeViewKey]
  );
  const dirty = (query || '').trim() !== (activeView?.query || '').trim();

  const parsed = useMemo(() => parseIssuesQuery(query), [query]);
  const filtered = useMemo(() => filterTasks(tasks, parsed), [tasks, parsed]);

  const total = filtered.length;

  const selectView = (view) => {
    setActiveViewKey(view.key);
    setQuery(view.query || '');
    setSuggestOpen(false);
  };

  // --- Autocomplete helpers ---------------------------------------
  const getSegmentAtCaret = useCallback(() => {
    const before = query.slice(0, caret);
    const lastSpace = before.lastIndexOf(' ');
    const start = lastSpace + 1;
    return { start, end: caret, text: before.slice(start) };
  }, [query, caret]);

  const asValueOption = (v) => ({ label: v, value: v, searchable: (v || '').toLowerCase() });

  const getValuePool = useCallback((field) => {
    if (field === 'priority') return ['Low', 'Medium', 'High', 'Urgent'].map(asValueOption);
    if (field === 'status') {
      const names = new Set();
      Object.values(statusCache).forEach(list => list.forEach(s => s?.name && names.add(s.name)));
      if (names.size === 0) tasks.forEach(t => t?.status_name && names.add(t.status_name));
      return [...names].map(asValueOption);
    }
    if (field === 'type') {
      const names = new Set();
      (taskTypes || []).forEach(t => t?.name && names.add(t.name));
      Object.values(taskTypeCache).forEach(list => list.forEach(t => t?.name && names.add(t.name)));
      return [...names].map(asValueOption);
    }
    if (field === 'assignee') {
      return (assignees || [])
        .filter(a => a.user_name)
        .map(a => ({
          label: a.employee_id ? `${a.user_name} (${a.employee_id})` : a.user_name,
          value: a.user_name,
          searchable: `${a.user_name || ''} ${a.employee_id || ''}`.toLowerCase(),
        }));
    }
    return [];
  }, [assignees, taskTypes, statusCache, taskTypeCache, tasks]);

  const suggestions = useMemo(() => {
    if (!suggestOpen) return [];
    const { text } = getSegmentAtCaret();
    const colonAt = text.indexOf(':');
    if (colonAt <= 0) {
      const prefix = text.toLowerCase();
      return AUTOCOMPLETE_FIELDS
        .filter(f => f.key.startsWith(prefix))
        .map(f => ({ kind: 'field', label: f.label, insert: `${f.key}:`, field: f.key }));
    }
    const field = text.slice(0, colonAt).toLowerCase();
    const prefix = text.slice(colonAt + 1).replace(/^"|"$/g, '').toLowerCase();
    if (!AUTOCOMPLETE_FIELDS.some(f => f.key === field)) return [];
    const values = getValuePool(field);
    const out = [
      { kind: 'any', label: `Any ${field}`, insert: '', field },
      { kind: 'no',  label: `No ${field}`, insert: `no:${field}`, field },
    ];
    values
      .filter(v => v.searchable.includes(prefix))
      .forEach(v => out.push({ kind: 'value', label: v.label, insert: `${field}:${quoteIfNeeded(v.value)}`, field }));
    return out;
  }, [suggestOpen, getSegmentAtCaret, getValuePool]);

  useEffect(() => { setSuggestIdx(0); }, [suggestions.length, query]);

  const applySuggestion = (sugg) => {
    const { start, end } = getSegmentAtCaret();
    const before = query.slice(0, start);
    const after = query.slice(end);
    let finalNext;
    let newCaret;
    if (sugg.kind === 'field') {
      finalNext = before + sugg.insert + after;
      newCaret = before.length + sugg.insert.length;
    } else {
      const ins = sugg.insert ? sugg.insert + ' ' : '';
      const merged = before + ins + after;
      finalNext = merged.replace(/\s+/g, ' ').replace(/^ /, '');
      newCaret = (before + ins).replace(/\s+/g, ' ').replace(/^ /, '').length;
    }
    setQuery(finalNext);
    setSuggestOpen(sugg.kind === 'field');
    requestAnimationFrame(() => {
      const el = searchInputRef.current?.input;
      if (el) {
        el.focus();
        el.setSelectionRange(newCaret, newCaret);
        setCaret(newCaret);
      }
    });
  };

  const onSearchChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    setCaret(e.target.selectionStart || v.length);
    setSuggestOpen(true);
  };

  const onSearchKeyDown = (e) => {
    if (!suggestOpen || suggestions.length === 0) {
      if (e.key === 'Escape') setSuggestOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSuggestIdx(i => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSuggestIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); applySuggestion(suggestions[suggestIdx]); }
    else if (e.key === 'Escape') { setSuggestOpen(false); }
  };

  // --- Saved views actions ----------------------------------------
  const openSaveModal = () => {
    const suggested = dirty ? (parsed.text ? parsed.text.slice(0, 40) : 'My view') : '';
    setSaveModalName(suggested);
    setSaveModalOpen(true);
  };
  const confirmSaveView = async () => {
    const name = saveModalName.trim();
    if (!name) { message.warning('Please enter a view name'); return; }
    try {
      const res = await axios.post('/project_board/task-views/', {
        project: projectId,
        name,
        query,
        position: savedViews.length,
      });
      const v = res.data;
      setSavedViews(prev => [...prev, v]);
      setActiveViewKey(`saved-${v.id}`);
      setSaveModalOpen(false);
      message.success('View saved');
    } catch { message.error('Failed to save view'); }
  };
  const deleteSavedView = async (view) => {
    try {
      await axios.delete(`/project_board/task-views/${view.id}/`);
      setSavedViews(prev => prev.filter(v => v.id !== view.id));
      if (activeViewKey === view.key) {
        setActiveViewKey(projectId ? DEFAULT_VIEW.key : null);
        setQuery('');
      }
      message.success('View deleted');
    } catch { message.error('Failed to delete view'); }
  };

  const confirmDeleteSavedView = (view) => {
    Modal.confirm({
      title: 'Delete this view?',
      content: <>The view <strong>{view.label}</strong> will be permanently removed for everyone on this project.</>,
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: () => deleteSavedView(view),
    });
  };

  const openRenameModal = (view) => {
    setRenameTarget(view);
    setRenameName(view.label);
    setRenameModalOpen(true);
  };
  const confirmRename = async () => {
    const name = renameName.trim();
    if (!renameTarget || !name) { message.warning('Please enter a name'); return; }
    try {
      await axios.patch(`/project_board/task-views/${renameTarget.id}/`, { name });
      setSavedViews(prev => prev.map(v => v.id === renameTarget.id ? { ...v, name } : v));
      setRenameModalOpen(false);
      message.success('View renamed');
    } catch { message.error('Failed to rename view'); }
  };

  const updateActiveViewQuery = async () => {
    if (!activeView || activeView.builtin || !activeView.id) return;
    try {
      await axios.patch(`/project_board/task-views/${activeView.id}/`, { query });
      setSavedViews(prev => prev.map(v => v.id === activeView.id ? { ...v, query } : v));
      message.success('View updated');
    } catch { message.error('Failed to update view'); }
  };

  // Lazy fetchers ----------------------------------------------------
  const ensureStatuses = async (projectId) => {
    if (!projectId || statusCache[projectId]) return statusCache[projectId] || [];
    try {
      const res = await axios.get(`/project_board/statuses/?project=${projectId}`);
      const list = res.data.results || res.data || [];
      setStatusCache(prev => ({ ...prev, [projectId]: list }));
      return list;
    } catch { return []; }
  };
  const ensureTypes = async (projectId) => {
    if (!projectId || taskTypeCache[projectId]) return taskTypeCache[projectId] || [];
    try {
      const res = await axios.get(`/project_board/task-types/?project=${projectId}`);
      const list = res.data.results || res.data || [];
      setTaskTypeCache(prev => ({ ...prev, [projectId]: list }));
      return list;
    } catch { return []; }
  };
  const ensureMembers = async (projectId) => {
    if (!projectId || memberCache[projectId]) return memberCache[projectId] || [];
    try {
      const res = await axios.get(`/project_board/members/?project=${projectId}&page_size=1000`);
      const rows = res.data.results || res.data || [];
      const list = rows.map(r => ({
        id: r.user_id,
        user_name: r.user_name || r.user_email,
        employee_id: r.employee_id,
      })).filter(m => m.id);
      setMemberCache(prev => ({ ...prev, [projectId]: list }));
      return list;
    } catch { return []; }
  };
  // PATCH helper -----------------------------------------------------
  const patchTask = async (task, patch, displayPatch) => {
    try {
      const res = await axios.patch(`/project_board/tasks/${task.id}/`, patch);
      onTaskUpdated && onTaskUpdated(task.id, { ...res.data, ...(displayPatch || {}) });
    } catch (e) {
      message.error('Failed to update');
    }
  };

  const saveTitle = async (task) => {
    const next = titleDraft.trim();
    setTitleEditId(null);
    if (!next || next === task.title) return;
    await patchTask(task, { title: next });
  };

  const updateAssignees = async (task, newUserIds) => {
    const current = (task.task_members || []).map(m => m.user).filter(Boolean);
    const toAdd = newUserIds.filter(id => !current.includes(id));
    const toRemove = (task.task_members || []).filter(m => m.user && !newUserIds.includes(m.user));
    try {
      const addResults = await Promise.all(
        toAdd.map(uid => axios.post('/project_board/task-members/', { task: task.id, user: uid }))
      );
      await Promise.all(
        toRemove.map(m => axios.delete(`/project_board/task-members/${m.id}/`))
      );
      // Rebuild task_members display
      const kept = (task.task_members || []).filter(m => newUserIds.includes(m.user));
      const added = addResults.map(r => r.data);
      const newMembers = [...kept, ...added];
      onTaskUpdated && onTaskUpdated(task.id, { task_members: newMembers });
    } catch (err) {
      // Surface the backend's reason (e.g. "User is not a member of this task's
      // project.") so the user understands why the assignment was rejected.
      const data = err?.response?.data;
      const reason = data?.user?.[0] || data?.error || data?.detail
        || (typeof data === 'string' ? data : null);
      message.error(reason || 'Failed to update assignees');
    }
  };

  const changeProject = async (task, newProjectId) => {
    // Move task to another project. Reset status to new project's initial status.
    const newStatuses = await ensureStatuses(newProjectId);
    const initial = newStatuses.find(s => s.is_initial) || newStatuses[0];
    const proj = (projects || []).find(p => p.id === newProjectId);
    const patch = { project: newProjectId };
    if (initial) patch.status = initial.id;
    await patchTask(task, patch, {
      project: newProjectId,
      project_code: proj?.project_code,
      project_name: proj?.name,
      status: initial?.id,
      status_name: initial?.name,
      status_color: initial?.color,
    });
  };

  // Column definitions ----------------------------------------------
  const columnDefs = useMemo(() => {
    const defs = [
      { key: '__code', label: '#', className: 'ghp-col-code' },
      { key: 'title', label: 'Title' },
      { key: 'type', label: 'Type' },
      { key: 'assignees', label: 'Assignees' },
      { key: 'status', label: 'Status' },
      { key: 'labels', label: 'Labels' },
    ];
    if (showProjectFilter) defs.push({ key: 'project', label: 'Repository' });
    defs.push({ key: 'due', label: 'Due Date' });
    defs.push({ key: 'start', label: 'Start Date' });
    defs.push({ key: 'created', label: 'Created' });
    return defs;
  }, [showProjectFilter]);

  // Cell renderers ---------------------------------------------------
  const renderTitle = (task) => {
    const closed = isStatusClosed(task.status_name);
    if (titleEditId === task.id) {
      return (
        <td key="title">
          <span className="ghp-title-cell">
            {closed ? <ClosedIcon /> : <OpenIcon />}
            <span className="ghp-title-edit">
              <input
                ref={titleInputRef}
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={() => saveTitle(task)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveTitle(task);
                  if (e.key === 'Escape') setTitleEditId(null);
                }}
              />
            </span>
          </span>
        </td>
      );
    }
    return (
      <td key="title">
        <span className="ghp-title-cell">
          {closed ? <ClosedIcon /> : <OpenIcon />}
          <span
            role="link"
            tabIndex={0}
            className="ghp-title-text"
            onClick={() => onOpenTask(task.id)}
            onKeyDown={(e) => { if (e.key === 'Enter') onOpenTask(task.id); }}
            title={task.title}
          >
            {task.title}
          </span>
          <span
            className="ghp-title-pencil"
            title="Rename"
            onClick={(e) => {
              e.stopPropagation();
              setTitleDraft(task.title || '');
              setTitleEditId(task.id);
            }}
          >
            <EditOutlined />
          </span>
        </span>
      </td>
    );
  };

  const typePopover = (task) => {
    const list = taskTypeCache[task.project] || taskTypes || [];
    const options = list.map(t => ({ value: t.id, label: t.name }));
    return (
      <FieldPopover
        title="Set type"
        options={options}
        value={task.task_type}
        onChange={(opt) => patchTask(task, { task_type: opt.value }, { task_type: opt.value, task_type_name: opt.label })}
        onClear={() => patchTask(task, { task_type: null }, { task_type: null, task_type_name: null })}
      />
    );
  };

  const statusPopover = (task) => {
    const list = statusCache[task.project] || [];
    const options = list.map(s => ({ value: s.id, label: s.name, _color: s.color }));
    return (
      <FieldPopover
        title="Set status"
        options={options}
        value={task.status}
        onChange={(opt) => {
          const s = list.find(x => x.id === opt.value);
          patchTask(task, { status: opt.value }, { status: opt.value, status_name: s?.name, status_color: s?.color });
        }}
        getDot={(opt) => opt._color}
      />
    );
  };

  const priorityPopover = (task) => {
    const options = [
      { value: 0, label: 'Low' },
      { value: 1, label: 'Medium' },
      { value: 2, label: 'High' },
      { value: 3, label: 'Urgent' },
    ];
    return (
      <FieldPopover
        title="Set priority"
        options={options}
        value={task.priority}
        onChange={(opt) => patchTask(task, { priority: opt.value }, { priority: opt.value })}
        getDot={(opt) => (PRIORITY_PALETTE[opt.label] || {}).fg}
      />
    );
  };

  const assigneePopover = (task) => {
    // Per-task: only members of THIS task's project, not the page-level list.
    // Falls back to the shared `assignees` prop until the per-project fetch lands.
    const list = memberCache[task.project] || assignees || [];
    const options = list.filter(a => a.id).map(a => ({
      value: a.id,
      label: a.employee_id ? `${a.user_name} (${a.employee_id})` : a.user_name,
    }));
    const currentIds = (task.task_members || []).map(m => m.user).filter(Boolean);
    return (
      <FieldPopover
        title="Assign up to 10 people"
        options={options}
        value={currentIds}
        multi
        onChange={(opt) => {
          const next = currentIds.includes(opt.value)
            ? currentIds.filter(id => id !== opt.value)
            : [...currentIds, opt.value];
          updateAssignees(task, next);
        }}
        onClear={() => updateAssignees(task, [])}
      />
    );
  };

  const projectPopover = (task) => {
    const options = (projects || []).map(p => ({ value: p.id, label: `${p.project_code ? p.project_code + ' · ' : ''}${p.name}` }));
    return (
      <FieldPopover
        title="Move to repository"
        options={options}
        value={task.project}
        onChange={(opt) => changeProject(task, opt.value)}
      />
    );
  };

  const datePopover = (task, field, displayKey) => (
    <div className="ghp-popover" style={{ padding: 10 }} onClick={(e) => e.stopPropagation()}>
      <DatePicker
        open
        value={task[field] ? dayjs(task[field]) : null}
        onChange={(d) => patchTask(task, { [field]: d ? d.format('YYYY-MM-DD') : null }, { [displayKey || field]: d ? d.format('YYYY-MM-DD') : null })}
        getPopupContainer={(t) => t.parentNode}
        style={{ width: 220 }}
      />
    </div>
  );

  const renderCell = (key, task) => {
    switch (key) {
      case '__code':
        return (
          <td key={key} className="ghp-col-code">
            {task.task_code ? <span className="ghp-code">{task.task_code}</span> : <span className="ghp-empty-cell">—</span>}
          </td>
        );
      case 'title':
        return renderTitle(task);
      case 'type':
        return (
          <td key={key} onClick={(e) => { e.stopPropagation(); ensureTypes(task.project); }}>
            <Dropdown trigger={['click']} dropdownRender={() => typePopover(task)} placement="bottomLeft">
              <span className="ghp-pill-cell" role="button">
                {task.task_type_name
                  ? <Pill label={task.task_type_name} palette={hashPalette(task.task_type_name)} />
                  : <span className="ghp-empty-cell">—</span>}
                <CaretDownOutlined className="ghp-cell-caret" />
              </span>
            </Dropdown>
          </td>
        );
      case 'assignees': {
        const members = task.task_members || [];
        return (
          <td key={key} onClick={(e) => { e.stopPropagation(); ensureMembers(task.project); }}>
            <Dropdown trigger={['click']} dropdownRender={() => assigneePopover(task)} placement="bottomLeft">
              <span className="ghp-assignees ghp-pill-cell" role="button">
                {members.length ? (
                  <>
                    <Avatar.Group maxCount={3} size="small">
                      {members.map(m => (
                        <Tooltip key={m.id} title={m.user_name}>
                          <Avatar size="small" style={{ backgroundColor: hashPalette(m.user_name || '').fg, color: '#fff', fontSize: 10 }}>
                            {(m.user_name || '?').charAt(0).toUpperCase()}
                          </Avatar>
                        </Tooltip>
                      ))}
                    </Avatar.Group>
                    {members.length === 1 && <span className="ghp-assignee-name">{members[0].user_name}</span>}
                  </>
                ) : <span className="ghp-empty-cell">—</span>}
                <CaretDownOutlined className="ghp-cell-caret" />
              </span>
            </Dropdown>
          </td>
        );
      }
      case 'status':
        return (
          <td key={key} onClick={(e) => { e.stopPropagation(); ensureStatuses(task.project); }}>
            <Dropdown trigger={['click']} dropdownRender={() => statusPopover(task)} placement="bottomLeft">
              <span className="ghp-pill-cell" role="button">
                {task.status_name
                  ? <Pill label={task.status_name} palette={STATUS_PALETTE(task.status_name)} />
                  : <span className="ghp-empty-cell">—</span>}
                <CaretDownOutlined className="ghp-cell-caret" />
              </span>
            </Dropdown>
          </td>
        );
      case 'labels': {
        const prio = priorityLabelColor(task.priority);
        return (
          <td key={key}>
            <Dropdown trigger={['click']} dropdownRender={() => priorityPopover(task)} placement="bottomLeft">
              <span className="ghp-labels-cell ghp-pill-cell" role="button">
                {prio && <Pill label={prio.label} palette={PRIORITY_PALETTE[prio.label]} />}
                {task.is_overdue && (
                  <Pill label="overdue" palette={{ bg: '#ffebe9', fg: '#cf222e', border: '#ffcecb' }} />
                )}
                <CaretDownOutlined className="ghp-cell-caret" />
              </span>
            </Dropdown>
          </td>
        );
      }
      case 'project':
        return (
          <td key={key}>
            <Dropdown trigger={['click']} dropdownRender={() => projectPopover(task)} placement="bottomLeft">
              <span className="ghp-pill-cell" role="button">
                {task.project_name || task.project_code ? (
                  <Pill
                    label={task.project_name || task.project_code}
                    palette={DEFAULT_PILL}
                    icon={<LockOutlined style={{ fontSize: 10 }} />}
                    className="is-lock"
                  />
                ) : <span className="ghp-empty-cell">—</span>}
                <CaretDownOutlined className="ghp-cell-caret" />
              </span>
            </Dropdown>
          </td>
        );
      case 'due':
        return (
          <td key={key}>
            <Dropdown trigger={['click']} dropdownRender={() => datePopover(task, 'due_date')} placement="bottomLeft">
              <span className="ghp-date-cell" role="button">
                {task.due_date ? (
                  <span style={{ color: task.is_overdue ? '#cf222e' : '#1f2328' }}>
                    {dayjs(task.due_date).format('MMM D, YYYY')}
                  </span>
                ) : <span className="ghp-empty-cell">—</span>}
              </span>
            </Dropdown>
          </td>
        );
      case 'start':
        return (
          <td key={key}>
            <Dropdown trigger={['click']} dropdownRender={() => datePopover(task, 'estimated_start_date')} placement="bottomLeft">
              <span className="ghp-date-cell" role="button">
                {task.estimated_start_date ? (
                  <span>{dayjs(task.estimated_start_date).format('MMM D, YYYY')}</span>
                ) : <span className="ghp-empty-cell">—</span>}
              </span>
            </Dropdown>
          </td>
        );
      case 'created':
        return (
          <td key={key}>
            {task.created_at ? (
              <span className="ghp-date-cell" title={dayjs(task.created_at).format('MMM D, YYYY h:mm A')}>
                {dayjs(task.created_at).format('MMM D, YYYY h:mm A')}
              </span>
            ) : <span className="ghp-empty-cell">—</span>}
          </td>
        );
      default:
        return <td key={key} />;
    }
  };

  const numberedItems = filtered;

  return (
    <div className="gh-projects">
      {/* Tabs row */}
      <div className="gh-projects__tabs">
        {allViews.map(v => {
          const isActive = activeViewKey === v.key;
          const tabButton = (
            <button
              key={v.key}
              type="button"
              className={`gh-projects__tab ${isActive ? 'is-active' : ''}`}
              onClick={() => selectView(v)}
            >
              <span className="tab-icon">
                {v.icon === 'board' ? <AppstoreOutlined /> : <TableOutlined />}
              </span>
              {v.label}
              {isActive && <DownOutlined className="tab-caret" style={{ fontSize: 9 }} />}
            </button>
          );
          if (v.builtin) return tabButton;
          // Saved view: only the project owner gets the manage menu (rename / delete).
          if (!canManageViews) return tabButton;
          const menuItems = {
            items: [
              { key: 'rename', label: 'Rename view', icon: <EditOutlined />, onClick: () => openRenameModal(v) },
              { type: 'divider' },
              { key: 'delete', label: 'Delete view', danger: true, icon: <DeleteOutlined />, onClick: () => confirmDeleteSavedView(v) },
            ],
          };
          return (
            <span key={v.key} className="gh-projects__tab-wrap">
              <Dropdown trigger={['contextMenu']} menu={menuItems}>
                {tabButton}
              </Dropdown>
              {isActive && (
                <Dropdown trigger={['click']} menu={menuItems} placement="bottomRight">
                  <button type="button" className="gh-projects__tab-menu" title="View options" onClick={(e) => e.stopPropagation()}>
                    <MoreOutlined />
                  </button>
                </Dropdown>
              )}
            </span>
          );
        })}
        {projectId && dirty && canManageViews && (
          activeView && !activeView.builtin && activeView.id ? (
            <button type="button" className="gh-projects__newview" onClick={updateActiveViewQuery} title="Save changes to this view">
              <SaveOutlined /> Save changes
            </button>
          ) : (
            <button type="button" className="gh-projects__newview" onClick={openSaveModal} title="Save current filters as a new view">
              <SaveOutlined /> Save view
            </button>
          )
        )}
      </div>

      {/* Search */}
      <div className="gh-projects__searchrow" style={{ position: 'relative' }}>
        <Input
          ref={searchInputRef}
          prefix={<SearchOutlined />}
          placeholder="Filter by keyword or by field (try typing 'status', 'type', 'priority', 'assignee')"
          value={query}
          onChange={onSearchChange}
          onKeyDown={onSearchKeyDown}
          onClick={(e) => { setCaret(e.target.selectionStart || 0); setSuggestOpen(true); }}
          onFocus={(e) => { setCaret(e.target.selectionStart || 0); setSuggestOpen(true); }}
          onBlur={() => { setTimeout(() => setSuggestOpen(false), 150); }}
          allowClear
        />
        {suggestOpen && suggestions.length > 0 && (
          <div className="ghp-autocomplete">
            {suggestions.map((s, idx) => (
              <div
                key={`${s.kind}-${s.label}-${idx}`}
                className={`ghp-autocomplete__item ${idx === suggestIdx ? 'is-active' : ''} ${s.kind === 'no' ? 'is-no' : ''} ${s.kind === 'any' ? 'is-any' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); applySuggestion(s); }}
                onMouseEnter={() => setSuggestIdx(idx)}
              >
                <span className="ghp-ac-kind">{s.kind === 'field' ? 'field' : s.kind === 'any' ? 'clear' : s.kind === 'no' ? 'empty' : s.field}</span>
                <span className="ghp-ac-label">{s.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        open={saveModalOpen}
        title="Save view"
        okText="Save"
        onCancel={() => setSaveModalOpen(false)}
        onOk={confirmSaveView}
      >
        <p style={{ marginBottom: 8, color: 'var(--ghp-text-muted)' }}>Name this view. It will be saved for this project.</p>
        <Input
          autoFocus
          value={saveModalName}
          onChange={(e) => setSaveModalName(e.target.value)}
          onPressEnter={confirmSaveView}
          placeholder="e.g. My open bugs"
        />
        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--ghp-text-muted)' }}>
          Filter: <code>{query || '(none)'}</code>
        </div>
      </Modal>

      <Modal
        open={renameModalOpen}
        title="Rename view"
        okText="Rename"
        onCancel={() => setRenameModalOpen(false)}
        onOk={confirmRename}
      >
        <Input
          autoFocus
          value={renameName}
          onChange={(e) => setRenameName(e.target.value)}
          onPressEnter={confirmRename}
          placeholder="View name"
        />
      </Modal>

      {/* Table */}
      <div className="gh-projects__tablewrap">
        <table className="gh-projects__table">
          <thead>
            <tr>
              {columnDefs.map(col => (
                <th key={col.key} className={col.className}>
                  <span className="ghp-colhead">
                    <span>{col.label}</span>
                    {col.key !== '__code' && (
                      <Dropdown
                        menu={{
                          items: [
                            { key: 'hide', label: 'Hide field' },
                            { key: 'sort-asc', label: 'Sort ascending' },
                            { key: 'sort-desc', label: 'Sort descending' },
                          ],
                        }}
                        trigger={['click']}
                      >
                        <span className="ghp-colmore" onClick={(e) => e.stopPropagation()}>
                          <MoreOutlined />
                        </span>
                      </Dropdown>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={columnDefs.length} className="gh-projects__empty">Loading…</td></tr>
            ) : numberedItems.length === 0 ? (
              <tr><td colSpan={columnDefs.length} className="gh-projects__empty">No items match your filter.</td></tr>
            ) : (
              numberedItems.map(task => (
                <tr key={task.id}>
                  {columnDefs.map(col => renderCell(col.key, task))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div
        className="gh-projects__footer"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
      >
        <span>
          {typeof totalCount === 'number' ? (
            <>
              {totalCount} {totalCount === 1 ? 'item' : 'items'}
              {total !== tasks.length && <> · {total} on this page after filters</>}
            </>
          ) : (
            <>{total} {total === 1 ? 'item' : 'items'}</>
          )}
        </span>
        {typeof totalCount === 'number' && totalCount > (pageSize || 20) && (
          <Pagination
            size="small"
            current={currentPage || 1}
            pageSize={pageSize || 20}
            total={totalCount}
            showSizeChanger={false}
            onChange={(p) => onPageChange && onPageChange(p)}
          />
        )}
      </div>
    </div>
  );
};

export default IssuesListView;
